import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  StoredSession,
  api,
  clearSession,
  loadSession,
  saveSession,
} from '@/api/client';

export interface AuthContextValue {
  session: StoredSession | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<StoredSession>;
  signOut: () => void;
  hasPermission: (permission: string) => boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

interface SignInResponse {
  // auth-ms shape
  signedAccessToken?: string;
  signedRefreshPayload?: string;
  // Optional pre-decoded fields (some callers add them) — we still prefer to
  // decode from the JWT so the FE never trusts an unsigned role field.
  accessToken?: string;
  refreshToken?: string;
  data?: SignInResponse;
}

interface JwtClaims {
  sub?: number | string;
  email?: string;
  role?: {
    id?: number;
    role_type?: string;
    permissions?: string[];
  };
}

/** Decode a JWT payload without verifying the signature (signature is the
 *  server's job — we just need to read the claims to populate the session). */
function decodeJwt(token: string): JwtClaims {
  const parts = token.split('.');
  if (parts.length < 2) throw new Error('Malformed JWT');
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '==='.slice((base64.length + 3) % 4);
  const json = atob(padded);
  try {
    return JSON.parse(
      decodeURIComponent(
        json
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    );
  } catch {
    return JSON.parse(json);
  }
}

function normalizeSignInResponse(raw: SignInResponse): StoredSession {
  const payload = raw?.data ?? raw;
  const accessToken = payload?.signedAccessToken ?? payload?.accessToken;
  const refreshToken = payload?.signedRefreshPayload ?? payload?.refreshToken;
  if (!accessToken) {
    throw new Error(
      'Sign-in succeeded but the response did not include an access token'
    );
  }

  const claims = decodeJwt(accessToken);
  if (!claims.role?.role_type) {
    throw new Error(
      'Sign-in succeeded but the issued token does not carry a role'
    );
  }

  return {
    accessToken,
    refreshToken,
    sub: Number(claims.sub ?? 0),
    email: claims.email ?? '',
    role: {
      id: claims.role.id ?? 0,
      role_type: claims.role.role_type,
      permissions: claims.role.permissions ?? [],
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(loadSession());

  useEffect(() => {
    const onExpired = () => setSession(null);
    window.addEventListener('bo:auth-expired', onExpired);
    return () => window.removeEventListener('bo:auth-expired', onExpired);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<SignInResponse>('/back-office/sign-in', {
      email,
      password,
    });
    const normalized = normalizeSignInResponse(data);
    saveSession(normalized);
    setSession(normalized);
    return normalized;
  }, []);

  const signOut = useCallback(() => {
    // Revoke the refresh token server-side so a leaked copy is useless. Fire
    // and forget — the local session is cleared either way.
    const refreshToken = loadSession()?.refreshToken;
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => undefined);
    }
    clearSession();
    setSession(null);
  }, []);

  const hasPermission = useCallback(
    (permission: string) => {
      if (!session) return false;
      if (session.role.role_type === 'super_admin') return true;
      return (session.role.permissions ?? []).includes(permission);
    },
    [session]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: !!session,
      signIn,
      signOut,
      hasPermission,
    }),
    [session, signIn, signOut, hasPermission]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
